import React, { CSSProperties } from "react";
import { Shell } from "@/shell/Shell";
import { PageHeader } from "@/shell/PageHeader";
import { FiltersBar } from "@/features/orders-analytics/components/FiltersBar";
import { useAvailableClients } from "@/features/orders-analytics/hooks/use-available-clients";
import { useOrdersAnalyticsFilters } from "@/features/orders-analytics/hooks/use-orders-analytics-filters";
import { useRiskData } from "@/features/orders-analytics/hooks/use-risk-data";
import { riskViewModel } from "@/features/orders-analytics/pages/risk-view-model";
import { SectionHeader } from "@/shell/components/SectionHeader";
import { StatusBanner } from "@/shell/components/StatusBanner";
import { KpiCard } from "@/shell/components/KpiCard";

const EMPTY_STATE_TEXT = "Sin datos disponibles — no hay fuente real para esta sección.";

const kycCoverageValue = (percent: number | null): string => (percent === null ? "—" : `${percent.toFixed(1)}%`);

export function RiskPage() {
    const { filters, setFilters } = useOrdersAnalyticsFilters();
    const { availableClients, loading, refresh } = useAvailableClients(filters);
    const { data, loading: riskLoading, error: riskError, refresh: refreshRisk } = useRiskData(filters);

    const vm = riskViewModel.fromData(data);
    const showError = riskError !== null;
    const showLoading = riskLoading && data === null && !showError;
    const showSections = !showError && !showLoading;

    const handleRefresh = () => {
        refresh();
        refreshRisk();
    };

    return (
        <Shell title="Risk, treasury & compliance" subtitle="Liquidity, fraud, controls">
            <PageHeader
                title="Risk, treasury & compliance"
                lede="Liquidity, settlement, fraud, AML, operational risk. The control-room view."
                right={
                    <FiltersBar
                        filters={filters}
                        availableClients={availableClients}
                        onFiltersChange={setFilters}
                        onRefresh={handleRefresh}
                        loading={loading || riskLoading}
                    />
                }
            />

            {showError ? (
                <StatusBanner
                    severity="critical"
                    title="No se pudo cargar el panel de riesgo"
                    sub="El backend de Metrics no pudo leer los datos de riesgo. Reintenta o avisa a ingeniería."
                    action={
                        <button type="button" className="btn btn--primary btn--sm" onClick={refreshRisk}>
                            Reintentar
                        </button>
                    }
                />
            ) : null}

            {showLoading ? (
                <div className="td-muted" style={loadingStyle}>
                    Cargando datos de riesgo…
                </div>
            ) : null}

            {showSections ? (
            <>
            <section className="section">
                <SectionHeader title="Treasury · liquidity by currency" meta="balance · prefunded · projected need · coverage" />
                <div className="td-muted" style={emptySectionStyle}>
                    {EMPTY_STATE_TEXT}
                </div>
            </section>

            <section className="section">
                <div className="grid-12">
                    <div style={{ gridColumn: "span 8" }}>
                        <SectionHeader
                            title="Provider settlement status"
                            count={vm.settlementByRail.length}
                            meta="settled orders/volume · in-flight orders/volume"
                        />
                        {vm.settlementByRail.length === 0 ? (
                            <div className="td-muted" style={emptySectionStyle}>
                                {EMPTY_STATE_TEXT}
                            </div>
                        ) : (
                            <div className="tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Rail</th>
                                            <th className="td-num">Settled orders</th>
                                            <th className="td-num">Settled volume</th>
                                            <th className="td-num">In-flight orders</th>
                                            <th className="td-num">In-flight volume</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.settlementByRail.map((row) => (
                                            <tr key={row.rail}>
                                                <td className="td-strong">{row.rail}</td>
                                                <td className="td-num">{row.settledOrders}</td>
                                                <td className="td-num td-strong">{row.settledVolume}</td>
                                                <td className="td-num">{row.inFlightOrders}</td>
                                                <td className="td-num">{row.inFlightVolume}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {vm.settlementByProvider.length > 0 ? (
                            <div className="tbl-wrap" style={providerTableWrapStyle}>
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Provider</th>
                                            <th className="td-num">Settled orders</th>
                                            <th className="td-num">Settled volume</th>
                                            <th className="td-num">In-flight orders</th>
                                            <th className="td-num">In-flight volume</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.settlementByProvider.map((row) => (
                                            <tr key={row.provider}>
                                                <td className="td-strong">{row.provider}</td>
                                                <td className="td-num">{row.settledOrders}</td>
                                                <td className="td-num td-strong">{row.settledVolume}</td>
                                                <td className="td-num">{row.inFlightOrders}</td>
                                                <td className="td-num">{row.inFlightVolume}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </div>

                    <div style={{ gridColumn: "span 4" }}>
                        <SectionHeader title="Blocked / stuck orders" meta="requires ops action" />
                        <div className="card" style={blockedCardStyle}>
                            {vm.problemOrders === null ? (
                                <div className="td-muted" style={emptySectionStyle}>
                                    {EMPTY_STATE_TEXT}
                                </div>
                            ) : (
                                <>
                                    <div className="ops-card__label">Currently blocked</div>
                                    <div className="num" style={blockedCountStyle}>
                                        {vm.problemOrders.blocked}
                                    </div>
                                    <div style={blockedVolumeStyle}>{vm.problemOrders.blockedVolume} volume at risk</div>
                                    <div style={blockedStatsRowStyle}>
                                        <div>
                                            <div className="ops-card__label">Stuck</div>
                                            <div style={blockedStatValueStyle}>{vm.problemOrders.stuck}</div>
                                        </div>
                                        <div>
                                            <div className="ops-card__label">Negative profit</div>
                                            <div style={blockedStatValueStyle}>{vm.problemOrders.negativeProfit}</div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            <section className="section">
                <SectionHeader title="Compliance & fraud" meta="under review · validation blocks · manual ops · KYC coverage" />
                {vm.compliance === null ? (
                    <div className="td-muted" style={emptySectionStyle}>
                        {EMPTY_STATE_TEXT}
                    </div>
                ) : (
                    <>
                        <div className="grid-4">
                            <KpiCard
                                icon="search"
                                label="Under review"
                                value={String(vm.compliance.underReview)}
                                sub="orders currently under manual review"
                                mom={null}
                                tint="primary"
                            />
                            <KpiCard
                                icon="block"
                                label="Validation blocked"
                                value={String(vm.compliance.validationBlocked)}
                                sub="orders blocked by validation"
                                mom={null}
                                tint="primary"
                            />
                            <KpiCard
                                icon="engineering"
                                label="Manual ops"
                                value={String(vm.compliance.manualOps)}
                                sub="orders requiring manual handling"
                                mom={null}
                                tint="primary"
                            />
                            <KpiCard
                                icon="verified_user"
                                label="KYC coverage"
                                value={kycCoverageValue(vm.compliance.kycCoveragePercent)}
                                sub="of orders with KYC-covered accounts"
                                mom={null}
                                tint="primary"
                            />
                        </div>

                        <div className="tbl-wrap" style={validationTableWrapStyle}>
                            {vm.compliance.validationBlockTypes.length === 0 ? (
                                <div className="td-muted" style={emptySectionStyle}>
                                    {EMPTY_STATE_TEXT}
                                </div>
                            ) : (
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Validation block type</th>
                                            <th className="td-num">Count</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.compliance.validationBlockTypes.map((row) => (
                                            <tr key={row.type}>
                                                <td className="td-strong">{row.type}</td>
                                                <td className="td-num">{row.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="td-muted" style={amlNoteStyle}>
                            AML & fraud signals: {EMPTY_STATE_TEXT}
                        </div>
                    </>
                )}
            </section>

            <section className="section">
                <SectionHeader title="Operational risk score" meta="0-100 · weighted inputs" />
                <div className="card" style={riskCardStyle}>
                    {vm.score === null ? (
                        <div className="td-muted" style={emptySectionStyle}>
                            {EMPTY_STATE_TEXT}
                        </div>
                    ) : (
                        <>
                            <div style={riskHeaderStyle}>
                                <div>
                                    <div className="ops-card__label">Risk score</div>
                                    <div className="num" style={riskLevelStyle}>
                                        {vm.score.value}
                                    </div>
                                </div>
                            </div>
                            <div className="tbl-wrap">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Input</th>
                                            <th className="td-num">Weight</th>
                                            <th className="td-num">Normalized</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {vm.score.inputs.map((input) => (
                                            <tr key={input.label}>
                                                <td className="td-strong">{input.label}</td>
                                                <td className="td-num">{input.weight}</td>
                                                <td className="td-num">{input.normalized}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            </section>
            </>
            ) : null}
        </Shell>
    );
}

const emptySectionStyle: CSSProperties = { padding: "16px 4px", fontSize: 13 };

const loadingStyle: CSSProperties = { padding: "24px 4px", fontSize: 13 };

const providerTableWrapStyle: CSSProperties = { marginTop: 12 };

const validationTableWrapStyle: CSSProperties = { marginTop: 12 };

const amlNoteStyle: CSSProperties = { padding: "10px 4px", fontSize: 12 };

const blockedCardStyle: CSSProperties = { padding: 18 };

const blockedCountStyle: CSSProperties = {
    fontFamily: "var(--vb-font-display)",
    fontWeight: 600,
    fontSize: 40,
    color: "var(--vb-error-500)",
    letterSpacing: "-0.02em",
    lineHeight: 1,
};

const blockedVolumeStyle: CSSProperties = {
    fontSize: 12,
    color: "var(--vb-fg-muted)",
    marginTop: 4,
};

const blockedStatsRowStyle: CSSProperties = {
    marginTop: 16,
    display: "flex",
    gap: 24,
};

const blockedStatValueStyle: CSSProperties = {
    fontWeight: 600,
    color: "var(--vb-fg-strong)",
    marginTop: 2,
};

const riskCardStyle: CSSProperties = { padding: 16 };

const riskHeaderStyle: CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    marginBottom: 12,
};

const riskLevelStyle: CSSProperties = {
    fontFamily: "var(--vb-font-display)",
    fontWeight: 600,
    fontSize: 32,
    color: "var(--vb-error-500)",
    letterSpacing: "-0.02em",
    lineHeight: 1,
};
